/**
 * Calculate the exact age in years from a date of birth.
 *
 * @param birthDate - The date of birth as a Date object
 * @returns The age in years, or null if the date is invalid
 */
export function calculateAge(birthDate: Date): number | null {
  if (!birthDate || !(birthDate instanceof Date) || Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Calculate age from a date string in YYYY-MM-DD format.
 *
 * @param dateString - The date string in YYYY-MM-DD format
 * @returns The age in years, or null if the date string is invalid
 */
export function calculateAgeFromString(dateString: string): number | null {
  if (!dateString) {
    return null;
  }

  // Parse YYYY-MM-DD format
  const dateParts = dateString.split("-");
  if (dateParts.length !== 3) {
    return null;
  }

  const yearStr = dateParts[0];
  const monthStr = dateParts[1];
  const dayStr = dateParts[2];

  if (!yearStr || !monthStr || !dayStr) {
    return null;
  }

  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(dayStr, 10);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  const date = new Date(year, month, day);

  // Check if the date is valid
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }

  return calculateAge(date);
}

/**
 * Check if a date string represents a valid date and calculate age.
 *
 * @param dateString - The date string in YYYY-MM-DD format
 * @returns Object with isValid flag and age, or null if invalid
 */
export function validateAndCalculateAge(dateString: string): { isValid: boolean; age: number | null } {
  if (!dateString) {
    return { isValid: false, age: null };
  }

  const age = calculateAgeFromString(dateString);

  if (age === null) {
    return { isValid: false, age: null };
  }

  return { isValid: true, age };
}
